import React, { useState, useCallback } from 'react'
import { Modal, Button, Table, Alert, ProgressBar, Badge } from 'react-bootstrap'
import { useMutation } from '@apollo/client'
import { parseCSV, ParsedContact, contactsToGraphQLInput } from '../../../utils/csvParser'
import { IMPORT_CAMPAIGN_CONTACTS } from '../../../gql/mutations/campaigns'
import './CSVImportModal.css'

interface CSVImportModalProps {
  show: boolean
  onHide: () => void
  onImportSuccess: (importedContactIds: string[]) => void
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'complete'

export const CSVImportModal: React.FC<CSVImportModalProps> = ({
  show,
  onHide,
  onImportSuccess,
}) => {
  const [step, setStep] = useState<ImportStep>('upload')
  const [dragActive, setDragActive] = useState(false)
  const [parsedData, setParsedData] = useState<ParsedContact[]>([])
  const [headers, setHeaders] = useState<string[]>([])
  const [stats, setStats] = useState({ valid: 0, errors: 0, total: 0 })
  const [importProgress, setImportProgress] = useState(0)
  const [importedIds, setImportedIds] = useState<string[]>([])
  const [importStats, setImportStats] = useState({ added: 0, tagged: 0 })

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }, [])

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0])
    }
  }

  const handleFile = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file')
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string
        const result = parseCSV(text)

        setParsedData(result.contacts)
        setHeaders(result.headers)
        setStats({
          valid: result.validCount,
          errors: result.errorCount,
          total: result.totalRows,
        })
        setStep('preview')
      } catch (error) {
        alert(`Error parsing CSV: ${error instanceof Error ? error.message : 'Unknown error'}`)
      }
    }
    reader.readAsText(file)
  }

  const [importCampaignContacts] = useMutation(IMPORT_CAMPAIGN_CONTACTS)

  const handleImport = async () => {
    setStep('importing')
    setImportProgress(20)
    setImportedIds([])
    setImportStats({ added: 0, tagged: 0 })

    try {
      // Filter only valid contacts
      const validContacts = parsedData.filter(c => c.errors.length === 0)
      const inputs = contactsToGraphQLInput(validContacts)
      
      if (inputs.length === 0) {
        alert('No valid contacts to import.')
        setStep('preview')
        return
      }

      setImportProgress(50)

      const response = await importCampaignContacts({
        variables: { contacts: inputs }
      })

      setImportProgress(100)

      const data = response.data?.importCampaignContacts
      if (data?.ok) {
        setImportedIds(data.importedIds || [])
        setImportStats({
          added: data.addedCount || 0,
          tagged: data.taggedExistingCount || 0
        })
        setStep('complete')
      } else {
        alert('Failed to import contacts. Please check your data.')
        setStep('preview')
      }

    } catch (error) {
      alert(`Error importing contacts: ${error instanceof Error ? error.message : 'Unknown error'}`)
      setStep('preview')
    }
  }

  const handleComplete = () => {
    onImportSuccess(importedIds)
    handleClose()
  }

  const handleClose = () => {
    setStep('upload')
    setParsedData([])
    setHeaders([])
    setStats({ valid: 0, errors: 0, total: 0 })
    setImportProgress(0)
    setImportedIds([])
    onHide()
  }

  const downloadTemplate = () => {
    const headers = ['first_name', 'last_name', 'email', 'phone']
    const example = ['John,Doe,john@example.com,+1234567890']
    const csvContent = [headers.join(','), ...example].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'contacts_template.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Modal show={show} onHide={handleClose} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title>Import Contacts from CSV</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className='csv-import-upload'>
            <div
              className={`csv-dropzone ${dragActive ? 'active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <i className='bi bi-cloud-upload fs-1 text-primary mb-3'></i>
              <h5>Drag and drop CSV file here</h5>
              <p className='text-muted'>or</p>
              <label htmlFor='csv-file-input' className='btn btn-primary'>
                <i className='bi bi-folder2-open me-2'></i>
                Choose File
              </label>
              <input
                id='csv-file-input'
                type='file'
                accept='.csv'
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
            </div>

            <Alert variant='info' className='mt-4'>
              <div className='d-flex justify-content-between align-items-start'>
                <div>
                  <Alert.Heading className='h6'>CSV Format Requirements:</Alert.Heading>
                  <ul className='mb-0'>
                    <li>Columns: <code>first_name, last_name, email, phone</code></li>
                    <li>Either email or phone is required</li>
                    <li>First row should contain column headers</li>
                    <li>Example: <code>John,Doe,john@example.com,+1234567890</code></li>
                  </ul>
                </div>
                <Button
                  variant='outline-info'
                  size='sm'
                  onClick={downloadTemplate}
                  className='bg-white text-info border-info'
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <i className='bi bi-download me-2'></i>
                  Download Template
                </Button>
              </div>
            </Alert>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 'preview' && (
          <div className='csv-import-preview'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div>
                <h6 className='mb-1'>Preview Data</h6>
                <div className='d-flex gap-3'>
                  <Badge bg='success'>
                    <i className='bi bi-check-circle me-1'></i>
                    {stats.valid} Valid
                  </Badge>
                  {stats.errors > 0 && (
                    <Badge bg='danger'>
                      <i className='bi bi-exclamation-circle me-1'></i>
                      {stats.errors} Errors
                    </Badge>
                  )}
                  <Badge bg='secondary'>
                    {stats.total} Total
                  </Badge>
                </div>
              </div>
            </div>

            <div className='table-container'>
              <Table striped bordered hover size='sm'>
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>#</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.slice(0, 50).map((contact, index) => (
                    <tr key={index} className={contact.errors.length > 0 ? 'table-danger' : ''}>
                      <td>{contact.rowNumber}</td>
                      <td>{contact.firstName}</td>
                      <td>{contact.lastName}</td>
                      <td>{contact.email}</td>
                      <td>{contact.phone}</td>
                      <td>
                        {contact.errors.length > 0 ? (
                          <span className='text-danger small'>
                            <i className='bi bi-exclamation-triangle me-1'></i>
                            {contact.errors[0]}
                          </span>
                        ) : (
                          <span className='text-success small'>
                            <i className='bi bi-check-circle me-1'></i>
                            Valid
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {parsedData.length > 50 && (
                <p className='text-muted text-center small'>
                  Showing first 50 of {parsedData.length} rows
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Importing */}
        {step === 'importing' && (
          <div className='csv-import-importing text-center py-5'>
            <div className='spinner-border text-primary mb-3' style={{ width: '3rem', height: '3rem' }}>
              <span className='visually-hidden'>Loading...</span>
            </div>
            <h5>Importing Contacts...</h5>
            <p className='text-muted'>Please wait while we create your contacts</p>
            <ProgressBar animated now={importProgress} className='mt-3' style={{ maxWidth: '400px', margin: '0 auto' }} />
            <p className='small text-muted mt-2'>{importProgress}%</p>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 'complete' && (
          <div className='csv-import-complete text-center py-5'>
            <i className='bi bi-check-circle-fill text-success' style={{ fontSize: '4rem' }}></i>
            <h4 className='mt-3'>Import Successful!</h4>
            <p className='text-muted mb-4'>
              {importedIds.length} contacts processed successfully.
            </p>
            <div className='d-flex justify-content-center gap-4 text-center'>
              <div className='p-3 border rounded bg-light min-w-150px'>
                <h2 className='text-primary fw-bold mb-1'>{importStats.added}</h2>
                <div className='small text-muted text-uppercase fw-semibold'>New Created</div>
              </div>
              <div className='p-3 border rounded bg-light min-w-150px'>
                <h2 className='text-warning fw-bold mb-1'>{importStats.tagged}</h2>
                <div className='small text-muted text-uppercase fw-semibold'>Existing Tagged</div>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === 'upload' && (
          <Button variant='secondary' onClick={handleClose}>
            Cancel
          </Button>
        )}
        {step === 'preview' && (
          <>
            <Button variant='secondary' onClick={() => setStep('upload')}>
              <i className='bi bi-arrow-left me-2'></i>
              Back
            </Button>
            <Button
              variant='primary'
              onClick={handleImport}
              disabled={stats.valid === 0}
            >
              <i className='bi bi-upload me-2'></i>
              Import {stats.valid} Contacts
            </Button>
          </>
        )}
        {step === 'complete' && (
          <Button variant='success' onClick={handleComplete}>
            <i className='bi bi-check2 me-2'></i>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
